import * as XLSX from "xlsx";
import { isOrdersExport, normalizeOrdersExport } from "./orders-export.js";
import type { CalendarDate, RawRow } from "./types.js";

/**
 * Parse a Shopify export (CSV / XLS / XLSX) into raw row objects, preserving
 * the `Day` (or `Month`) column as a timezone-free CalendarDate.
 *
 * We deliberately AVOID converting to a JS Date during processing: the only
 * source of truth is the literal text in the spreadsheet, and JS Dates
 * inevitably get interpreted in the user's local timezone, which caused
 * "Dec 1 → Nov 30" drift for users east of UTC.
 *
 * Parser options worth calling out:
 *   - `cellDates: false` — date cells come back as Excel serial numbers
 *     (timezone-neutral) rather than JS Dates (timezone-tainted). We decode
 *     them ourselves below.
 *   - `dense: true` — store cells in a 2-D array instead of one giant
 *     `{address: cell}` object. Avoids V8's "Too many properties to
 *     enumerate" limit when the spreadsheet has many cells, and lets
 *     `sheet_to_json` skip empty rows efficiently.
 *   - `nodim: true` — IGNORE the worksheet's self-reported `!ref` and rebuild
 *     it from the cells that are actually populated. Some Shopify exports
 *     (and other third-party exporters) declare wildly oversized ranges
 *     (e.g. `A1:XFD1048576`); without this flag, `sheet_to_json` would
 *     iterate the entire declared range and either freeze the tab or throw
 *     "Too many properties to enumerate". With this flag, only the truly
 *     populated cells determine the range.
 */
export function parseShopifyUpload(input: ArrayBuffer | Uint8Array): RawRow[] {
  const data = input instanceof Uint8Array ? input : new Uint8Array(input);
  let rows = readFirstSheetRows(data, false);

  if (isOrdersExport(rows)) {
    // Shopify "Orders" export (one row per line item). Re-read with `raw: true`
    // so CSV text cells stay literal strings: `Created at` looks like
    // "2022-05-31 21:50:39 -0700" and we only want its calendar date, and
    // zips must keep their leading zeros. Then map onto sales-report columns.
    rows = normalizeOrdersExport(readFirstSheetRows(data, true));
  }

  for (const row of rows) {
    // Shopify exports vary: some have a `Day` column (per-order date), others
    // (the "Tax Report with Order Details" CSV grouped by month) have a
    // `Month` column. Either way it's a calendar day, normalize to `Day`.
    const rawDate = row["Day"] ?? row["Month"];
    row["Day"] = coerceCalendarDate(rawDate);
  }
  return rows;
}

/**
 * Read the first sheet into row objects. `raw` is passed to SheetJS's reader:
 * when true, CSV cells are not type-sniffed and come back as strings.
 */
function readFirstSheetRows(data: Uint8Array, raw: boolean): RawRow[] {
  const wb = XLSX.read(data, {
    type: "array",
    cellDates: false,
    dense: true,
    nodim: true,
    raw,
  });
  const firstSheetName = wb.SheetNames[0];
  if (!firstSheetName) {
    throw new Error("Uploaded file contains no sheets");
  }
  const ws = wb.Sheets[firstSheetName];
  if (!ws) {
    throw new Error("Uploaded file's first sheet is empty");
  }

  // Belt-and-suspenders: even with `nodim: true`, recompute the range from
  // the dense cell array so any lingering oversized `!ref` is replaced. This
  // is cheap (O(populated cells)) and protects against parser quirks.
  const tightRef = computeTightRangeDense(ws);
  if (tightRef) ws["!ref"] = tightRef;

  return XLSX.utils.sheet_to_json<RawRow>(ws, {
    raw: true,
    defval: null,
  });
}

/**
 * Compute a tight A1 range over the cells that actually contain a value,
 * using SheetJS's dense `!data` array. Returns null if the sheet is empty
 * or not in dense form (callers should leave the existing `!ref` alone in
 * that case).
 */
function computeTightRangeDense(ws: XLSX.WorkSheet): string | null {
  const dense = (ws as { "!data"?: Array<Array<XLSX.CellObject | undefined> | undefined> })["!data"];
  if (!Array.isArray(dense)) return null;

  let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
  for (let r = 0; r < dense.length; r++) {
    const rowArr = dense[r];
    if (!rowArr) continue;
    for (let c = 0; c < rowArr.length; c++) {
      const cell = rowArr[c];
      if (!cell || cell.v == null || cell.v === "") continue;
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
    }
  }

  if (!isFinite(minR) || !isFinite(maxR)) return null;
  return XLSX.utils.encode_range({ s: { r: minR, c: minC }, e: { r: maxR, c: maxC } });
}

function coerceCalendarDate(value: unknown): CalendarDate | null {
  if (value == null || value === "") return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    // YYYY-MM-DD (most common Shopify format) — and optional trailing time.
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(trimmed);
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
    // M/D/YYYY or MM/DD/YYYY.
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(trimmed);
    if (m) return { y: +m[3], m: +m[1], d: +m[2] };
    return null;
  }

  if (typeof value === "number") {
    return excelSerialToCalendarDate(value);
  }

  if (value instanceof Date) {
    // Fallback for some edge cases (e.g. if SheetJS still produced a Date).
    // We use the LOCAL components because that's what the user saw on screen
    // when they created the export. UTC components could be off by a day for
    // dates that were stored as local-midnight on a non-UTC machine.
    if (isNaN(value.getTime())) return null;
    return {
      y: value.getFullYear(),
      m: value.getMonth() + 1,
      d: value.getDate(),
    };
  }

  return null;
}

/**
 * Decode an Excel 1900-system serial number into a calendar date. This is
 * timezone-independent because we work purely in days since Excel's epoch.
 */
function excelSerialToCalendarDate(serial: number): CalendarDate | null {
  if (!isFinite(serial)) return null;
  // Excel's nominal epoch is 1900-01-00 (i.e. days since Dec 30 1899 in the
  // 1900 date system, because Excel mistakenly treats 1900 as a leap year).
  // Using UTC math here is just a convenient timezone-neutral calendar; no
  // wall-clock interpretation is involved.
  const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86400000;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return {
    y: d.getUTCFullYear(),
    m: d.getUTCMonth() + 1,
    d: d.getUTCDate(),
  };
}
